import axios from 'axios';
import type { UserRole } from '../types/auth';

const baseURL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';

export const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

export const setAuthToken = (token: string | null): void => {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }

  delete apiClient.defaults.headers.common.Authorization;
};

// Dashboard metrics
export interface DashboardMetrics {
  activeMembers: number;
  pendingMembers: number;
  vipMembers: number;
  familyMembers: number;
  individualMembers: number;
  upcomingEvents: number;
  todayCheckIns: number;
  drawingPoolSize: number;
}

interface PaginatedPayload<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface MembersResponse {
  success: boolean;
  data:
    | Array<{ id: string; fullName?: string; full_name?: string; email: string; status: string; tier: string }>
    | PaginatedPayload<{ id: string; fullName?: string; full_name?: string; email: string; status: string; tier: string }>;
  timestamp: string;
}

interface EventsResponse {
  success: boolean;
  data:
    | Array<{ id: string; name: string; date: string; time?: string | null; location?: string | null; description?: string | null; isActive?: boolean; is_active?: number }>
    | PaginatedPayload<{ id: string; name: string; date: string; time?: string | null; location?: string | null; description?: string | null; isActive?: boolean; is_active?: number }>;
  timestamp: string;
}

const toEvent = (row: { id: string; name: string; date: string; time?: string | null; location?: string | null; description?: string | null; isActive?: boolean; is_active?: number }): Event => ({
  id: row.id,
  name: row.name,
  date: row.date,
  time: row.time ?? null,
  location: row.location ?? null,
  description: row.description ?? null,
  isActive: row.isActive ?? Boolean(row.is_active),
});

interface EligibleResponse {
  success: boolean;
  data: Array<{ id: string; fullName: string; email: string }>;
  timestamp: string;
}

const readCollection = <T>(payload: T[] | PaginatedPayload<T>): { items: T[]; total: number } => {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  }

  return { items: payload.items ?? [], total: payload.total ?? payload.items.length };
};

export const fetchDashboardMetrics = async (role: UserRole): Promise<DashboardMetrics> => {
  try {
    const canViewMembers = role !== 'scanner';

    let activeMembers = 0;
    let pendingMembers = 0;
    let vipMembers = 0;
    let familyMembers = 0;
    let individualMembers = 0;

    if (canViewMembers) {
      const membersRes = await apiClient.get<MembersResponse>('/api/v1/admin/members?pageSize=100');
      const members = readCollection(membersRes.data.data).items.map((row) => toMember(row));
      activeMembers = members.filter((member) => member.status === 'Paid').length;
      pendingMembers = members.filter((member) => member.status === 'Pending Payment').length;
      vipMembers = members.filter((member) => member.tier === 'VIP').length;
      familyMembers = members.filter((member) => member.tier === 'Family').length;
      individualMembers = members.filter((member) => member.tier === 'Individual').length;
    }

    const eventsRes = await apiClient.get<EventsResponse>('/api/v1/admin/events?pageSize=100');
    const events = readCollection(eventsRes.data.data).items.map(toEvent);
    const activeEvents = events.filter((e) => e.isActive).length;

    const checkIns = 0;
    let drawingPoolSize = 0;

    if (canViewMembers && events.length > 0) {
      const firstEvent = events[0];
      try {
        const eligibleRes = await apiClient.get<EligibleResponse>(
          `/api/v1/admin/events/${firstEvent.id}/drawing/eligible`
        );
        drawingPoolSize = eligibleRes.data.data.length;
      } catch {
        drawingPoolSize = 0;
      }
    }

    return {
      activeMembers,
      pendingMembers,
      vipMembers,
      familyMembers,
      individualMembers,
      upcomingEvents: activeEvents,
      todayCheckIns: checkIns,
      drawingPoolSize: drawingPoolSize,
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metrics:', error);
    throw error;
  }
};

export interface Member {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  status: 'Paid' | 'Pending Payment' | 'Inactive';
  tier: 'Individual' | 'Couple' | 'Family' | 'VIP';
}

export interface Event {
  id: string;
  name: string;
  date: string;
  time: string | null;
  location: string | null;
  description: string | null;
  isActive: boolean;
}

export interface CreateEventPayload {
  name: string;
  date: string;
  time?: string;
  location?: string;
  description?: string;
  createdByUserId?: string;
}

export interface CreateMemberPayload {
  firstName: string;
  lastName: string;
  email: string;
  tier: 'Individual' | 'Couple' | 'Family' | 'VIP';
  status: 'Paid' | 'Pending Payment';
}

const splitName = (fullName: string): { firstName: string; lastName: string } => {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const normalizeStatus = (status: string): 'Paid' | 'Pending Payment' | 'Inactive' => {
  if (status === 'Active' || status === 'Paid') return 'Paid';
  if (status === 'Pending' || status === 'Pending Payment') return 'Pending Payment';
  return 'Inactive';
};

const normalizeTier = (tier: string): 'Individual' | 'Couple' | 'Family' | 'VIP' => {
  if (tier === 'Couple') return 'Couple';
  if (tier === 'Family') return 'Family';
  if (tier === 'VIP') return 'VIP';
  return 'Individual';
};

const toMember = (record: {
  id: string;
  fullName?: string;
  full_name?: string;
  email: string;
  status: string;
  tier: string;
}): Member => {
  const fullName = (record.fullName ?? record.full_name ?? '').trim();
  const names = splitName(fullName);
  return {
    id: record.id,
    fullName,
    firstName: names.firstName,
    lastName: names.lastName,
    email: record.email,
    status: normalizeStatus(record.status),
    tier: normalizeTier(record.tier),
  };
};

export const fetchMembers = async (pageSize: number = 10): Promise<Member[]> => {
  try {
    const res = await apiClient.get<MembersResponse>(`/api/v1/admin/members?pageSize=${pageSize}`);
    return readCollection(res.data.data).items.map((row) => toMember(row));
  } catch (error) {
    console.error('Failed to fetch members:', error);
    throw error;
  }
};

export const fetchEvents = async (pageSize: number = 10): Promise<Event[]> => {
  try {
    const res = await apiClient.get<EventsResponse>(`/api/v1/admin/events?pageSize=${pageSize}`);
    return readCollection(res.data.data).items.map(toEvent);
  } catch (error) {
    console.error('Failed to fetch events:', error);
    throw error;
  }
};

export const createMemberRecord = async (payload: CreateMemberPayload): Promise<Member> => {
  const fullName = `${payload.firstName.trim()} ${payload.lastName.trim()}`.trim();
  const response = await apiClient.post<{
    success: boolean;
    data: { id: string; full_name: string; email: string; status: string; tier: string };
    timestamp: string;
  }>('/api/v1/admin/members', {
    fullName,
    email: payload.email,
    tier: payload.tier,
    status: payload.status,
  });

  return toMember({
    id: response.data.data.id,
    fullName: response.data.data.full_name,
    email: response.data.data.email,
    status: response.data.data.status,
    tier: response.data.data.tier,
  });
};

export const resendMemberCard = async (memberId: string): Promise<void> => {
  await apiClient.post(`/api/v1/admin/members/${memberId}/resend-card`);
};

export const createEvent = async (payload: CreateEventPayload): Promise<Event> => {
  const response = await apiClient.post<{
    success: boolean;
    data: {
      id: string;
      name: string;
      date: string;
      time: string | null;
      location: string | null;
      description: string | null;
      is_active: number;
    };
    timestamp: string;
  }>('/api/v1/admin/events', payload);
  const d = response.data.data;
  return {
    id: d.id,
    name: d.name,
    date: d.date,
    time: d.time ?? null,
    location: d.location ?? null,
    description: d.description ?? null,
    isActive: Boolean(d.is_active),
  };
};
